import { useWindowStore } from '@/wm/store';
import { getApp } from '@/apps/registry';
import { associationFor } from '@/config/fileAssociations';
import { play } from '@/lib/sound';
import type { FsNode } from './types';

interface OpenOpts {
  parentId?: string;
}

/** External schemes never make sense inside a frame. */
function isExternalScheme(href: string): boolean {
  return /^(mailto:|tel:|sms:|magnet:)/i.test(href);
}

function openExternally(href: string): undefined {
  window.open(href, '_blank', 'noopener,noreferrer');
  return undefined;
}

function notInstalled(name: string, opts?: OpenOpts): string | undefined {
  play('error');
  return useWindowStore.getState().open(
    'msgbox',
    {
      title: 'Windows',
      icon: 'error',
      text: `Windows cannot open this file:\n\n${name}\n\nThe program required to open it is not installed on this computer.`,
      buttons: ['OK'],
    },
    { parentId: opts?.parentId },
  );
}

/**
 * The single entry point for "the user activated this thing". Desktop icons,
 * Explorer, the Start menu and Notepad's Open dialog all funnel through here,
 * which is why adding a file type never means touching more than one place.
 */
export function openFile(node: FsNode, opts: OpenOpts = {}): string | undefined {
  const wm = useWindowStore.getState();

  if (node.type === 'folder') {
    return wm.open('explorer', { path: node.path });
  }

  switch (node.data.kind) {
    case 'app': {
      const { appId, args } = node.data;
      if (!getApp(appId)) return notInstalled(node.name, opts);
      return wm.open(appId, args ?? {});
    }

    case 'url': {
      const { href, openMode } = node.data;
      if (openMode === 'newTab' || isExternalScheme(href)) return openExternally(href);
      return wm.open('ie', { url: href });
    }

    case 'text':
    case 'markdown': {
      const assoc = associationFor(node.ext);
      const appId = assoc && assoc.appId !== '*' ? assoc.appId : 'notepad';
      if (!getApp(appId)) return notInstalled(node.name, opts);
      return wm.open(appId, { [assoc?.argKey ?? 'file']: node.path });
    }

    case 'audio': {
      // A track, not a path: the file *is* the track, and the player takes it
      // by id. Which player is the association's business, so putting Winamp
      // on `.mp3` would send double-clicks there instead.
      //
      // `autoplay` because activating a sound file has always meant "play
      // this", never "select this" — and the double-click that got here is
      // the gesture browsers want before any audio, so the player may start
      // on its own. Opened from the Start menu it still comes up stopped.
      const appId = associationFor(node.ext)?.appId ?? 'mediaplayer';
      if (!getApp(appId)) return notInstalled(node.name, opts);
      return wm.open(appId, { track: node.data.trackId, autoplay: true });
    }

    case 'playlist': {
      // Nothing is passed: the player's playlist *is* this file's contents, so
      // opening it means opening the player, at the top of the list.
      const appId = associationFor(node.ext)?.appId ?? 'winamp';
      if (!getApp(appId)) return notInstalled(node.name, opts);
      return wm.open(appId, {});
    }

    case 'image': {
      const assoc = associationFor(node.ext);
      const appId = assoc?.appId ?? 'imageviewer';
      if (!getApp(appId)) return notInstalled(node.name, opts);
      return wm.open(appId, { file: node.path });
    }

    default:
      return notInstalled(node.name, opts);
  }
}
